import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { GraduationCap, BookOpen, School, Award, Calendar } from "lucide-react";

const educationData = [
  {
    institutionKey: "education.items.rupp.institution",
    degreeKey: "education.items.rupp.degree",
    descKey: "education.items.rupp.desc",
    period: "2021 — 2025",
    tag: "Higher Education",
    icon: GraduationCap,
    isCurrent: true,
  },
  {
    institutionKey: "education.items.beltei.institution",
    degreeKey: "education.items.beltei.degree",
    descKey: "education.items.beltei.desc",
    period: "2015 — 2020",
    tag: "Language & Communication",
    icon: BookOpen,
  },
  {
    institutionKey: "education.items.sonthormokHigh.institution",
    degreeKey: "education.items.sonthormokHigh.degree",
    descKey: "education.items.sonthormokHigh.desc",
    period: "2015 — 2021",
    tag: "Secondary Education",
    icon: School,
  },
  {
    institutionKey: "education.items.sonthormokPrimary.institution",
    degreeKey: "education.items.sonthormokPrimary.degree",
    descKey: "education.items.sonthormokPrimary.desc",
    period: "2009 — 2015",
    tag: "Foundational Education",
    icon: Award,
  },
];

export default function Education() {
  const { t } = useTranslation();

  return (
    <section
      id="education"
      className="py-20 px-6 sm:px-12 lg:px-20 bg-white dark:bg-black text-neutral-900 dark:text-white relative border-t border-neutral-200/60 dark:border-neutral-900"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-block py-1 px-3 rounded-full bg-neutral-100 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-300 text-xs font-semibold tracking-wider uppercase mb-3 border border-neutral-200/80 dark:border-neutral-800">
            {t("education.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-neutral-900 dark:text-white">
            {t("education.title")}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 max-w-xl mx-auto text-base sm:text-lg">
            {t("education.desc")}
          </p>
        </motion.div>

        {/* Clean, Non-Decorative Vertical Timeline */}
        <div className="relative pl-6 sm:pl-10 border-l border-neutral-200 dark:border-neutral-800 space-y-10">
          {educationData.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -15 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="relative group"
              >
                {/* Clean timeline dot indicator */}
                <div className="absolute -left-[31px] sm:-left-[47px] top-1.5 w-7 h-7 rounded-lg bg-neutral-100 dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-300 group-hover:border-blue-500 group-hover:text-blue-500 transition-colors">
                  <Icon size={14} />
                </div>

                {/* Content Card */}
                <div className="p-6 rounded-2xl bg-neutral-50/70 dark:bg-neutral-950/60 border border-neutral-200/80 dark:border-neutral-800/80 group-hover:border-neutral-300 dark:group-hover:border-neutral-700 transition-all duration-200">
                  {/* Period & Tag */}
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 dark:text-neutral-400 font-mono">
                      <Calendar size={13} />
                      <span>{item.period}</span>
                    </span>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-200/60 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300">
                      {item.tag}
                    </span>
                  </div>

                  {/* Degree */}
                  <h3 className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white mb-1">
                    {t(item.degreeKey)}
                  </h3>

                  {/* Institution */}
                  <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-3">
                    {t(item.institutionKey)}
                  </p>

                  {/* Description */}
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    {t(item.descKey)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
